import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getConsent,
  saveConsent,
  applyConsentToGtag,
  initConsentFromStorage,
} from './consent';

export default function ConsentBanner() {
  // If you use a namespace, e.g. useTranslation('common')
  const { t } = useTranslation(); // or useTranslation('common')

  const [open, setOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [ads, setAds] = useState(false);
  const [personalizedAds, setPersonalizedAds] = useState(false);

  // Allow reopening the banner later (e.g., from footer link)
  useEffect(() => {
    window.openCookiePrefs = () => {
      const existing = getConsent();
      if (existing) {
        setAnalytics(!!existing.analytics);
        setAds(!!existing.ads);
        setPersonalizedAds(!!existing.personalizedAds);
      }
      setOpen(true);
    };
    return () => delete window.openCookiePrefs;
  }, []);

  // On load: apply stored consent or show banner
  useEffect(() => {
    const existing = getConsent();
    if (existing) {
      initConsentFromStorage();
      setAnalytics(!!existing.analytics);
      setAds(!!existing.ads);
      setPersonalizedAds(!!existing.personalizedAds);
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, []);

  const persist = useCallback((value) => {
    saveConsent(value);
    applyConsentToGtag(value);
    window.dataLayer?.push({ event: 'consent_update', consent: value });
    setOpen(false);
  }, []);

  const acceptAll = () => persist({ analytics: true, ads: true, personalizedAds: true });
  const rejectAll = () => persist({ analytics: false, ads: false, personalizedAds: false });
  const saveChoices = () => persist({ analytics, ads, personalizedAds });

  if (!open) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-50">
      <div className="bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/80 border-t">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="py-4 md:py-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
              {/* Text + toggles */}
              <div className="max-w-2xl">
                <h3 className="font-semibold text-gray-900">
                  {t('consent.title')}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {t('consent.desc')}
                </p>

                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  <label className="inline-flex items-center gap-2 text-gray-700">
                    <input type="checkbox" checked disabled className="accent-violet-600" />
                    {t('consent.essential')}
                  </label>

                  <label className="inline-flex items-center gap-2 text-gray-700">
                    <input
                      type="checkbox"
                      className="accent-violet-600"
                      checked={analytics}
                      onChange={(e) => setAnalytics(e.target.checked)}
                    />
                    {t('consent.analytics')}
                  </label>

                  <label className="inline-flex items-center gap-2 text-gray-700">
                    <input
                      type="checkbox"
                      className="accent-violet-600"
                      checked={ads}
                      onChange={(e) => setAds(e.target.checked)}
                    />
                    {t('consent.ads')}
                  </label>

                  <label className="inline-flex items-center gap-2 text-gray-700">
                    <input
                      type="checkbox"
                      className="accent-violet-600"
                      checked={personalizedAds}
                      onChange={(e) => setPersonalizedAds(e.target.checked)}
                    />
                    {t('consent.adsPersonal')}
                  </label>
                </div>
              </div>

              {/* Buttons */}
              <div className="shrink-0 flex flex-wrap gap-2">
                <button
                  onClick={acceptAll}
                  className="px-4 py-2 rounded-lg bg-violet-600 text-white hover:bg-violet-700 transition"
                >
                  {t('consent.accept')}
                </button>
                <button
                  onClick={rejectAll}
                  className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition"
                >
                  {t('consent.reject')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
